import { Autocomplete, Box, TextField } from '@mui/material';
import * as React from 'react';
import { Controller } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { useGetCompanyOffices } from '~/data/companyOffices';
import { TEXT_MAX_LENGTH } from '~/features/OverlappingEmploymentDialog/data';

import { OverlappingEmploymentFormValues } from '../../hooks/useOverlappingEmploymentForm';
import { useSxStyles } from '../../useStyles';

interface Props {
  formValues: OverlappingEmploymentFormValues;
}

export const ContactDataTabForm = ({ formValues }: Props) => {
  const { t } = useTranslation();
  const sxStyles = useSxStyles();
  const {
    formProps: { control },
  } = formValues;
  const { data: offices } = useGetCompanyOffices();
  const requiredText = t('fieldIsRequired');

  return (
    <Box sx={sxStyles.container}>
      <Controller
        control={control}
        name={'overlappingEmployment.contact.fEmpPhoneWork'}
        rules={{
          pattern: {
            value: /^\+?[0-9]*$/gi,
            message: t('phoneNumberInvalid'),
          },
          maxLength: {
            value: TEXT_MAX_LENGTH,
            message: t('laMaxLengthRequired'),
          },
        }}
        render={({ field: { onChange, value }, fieldState: { error } }) => (
          <TextField
            value={value}
            sx={{
              ...sxStyles.selectStyle,
              ...sxStyles.inputBottomMargin,
            }}
            onChange={onChange}
            label={t('fEmpPhoneWork')}
            error={!!error}
            helperText={error?.message}
          />
        )}
      />

      <Controller
        control={control}
        name={'overlappingEmployment.contact.fEmpPhoneMob'}
        rules={{
          pattern: {
            value: /^\+?[0-9]*$/gi,
            message: t('phoneNumberInvalid'),
          },
          maxLength: {
            value: TEXT_MAX_LENGTH,
            message: t('laMaxLengthRequired'),
          },
        }}
        render={({ field: { onChange, value }, fieldState: { error } }) => (
          <TextField
            value={value}
            sx={{
              ...sxStyles.selectStyle,
              ...sxStyles.inputBottomMargin,
            }}
            onChange={onChange}
            label={t('fEmpPhoneMob')}
            error={!!error}
            helperText={error?.message}
          />
        )}
      />

      <Controller
        control={control}
        name={'overlappingEmployment.contact.fEmpEmailWork'}
        rules={{
          pattern: {
            value: /^\S+@\S+$/i,
            message: t('emailInvalid'),
          },
        }}
        render={({ field: { onChange, value }, fieldState: { error } }) => (
          <TextField
            value={value}
            sx={{
              ...sxStyles.selectStyle,
              ...sxStyles.inputBottomMargin,
            }}
            onChange={onChange}
            label={t('fEmpEmailWork')}
            error={!!error}
            helperText={error?.message}
          />
        )}
      />

      <Controller
        control={control}
        name="overlappingEmployment.contact.fEmpOfficeLoc"
        rules={{ required: requiredText }}
        render={({ field: { onChange, value }, fieldState: { error } }) => (
          <Autocomplete
            options={offices}
            getOptionLabel={(o) => o.fOfficeName}
            isOptionEqualToValue={(o, v) => o.fOfficeId === v.fOfficeId}
            onChange={(_, value) => onChange(value?.fOfficeId?.toString())}
            value={
              offices.find((office) => office.fOfficeId === +value) || null
            }
            renderInput={(params) => (
              <TextField
                {...params}
                required
                label={t('fEmpOfficeLoc')}
                error={!!error}
                helperText={error?.message}
              />
            )}
            sx={{
              ...sxStyles.selectStyle,
              ...sxStyles.inputBottomMargin,
            }}
          />
        )}
      />

      <Controller
        control={control}
        name={'overlappingEmployment.contact.fEmpOfficeFloor'}
        rules={{
          maxLength: {
            value: TEXT_MAX_LENGTH,
            message: t('laMaxLengthRequired'),
          },
        }}
        render={({ field: { onChange, value }, fieldState: { error } }) => (
          <TextField
            value={value}
            sx={{
              ...sxStyles.selectStyle,
              ...sxStyles.inputBottomMargin,
            }}
            onChange={onChange}
            label={t('fEmpOfficeFloor')}
            error={!!error}
            helperText={error?.message}
          />
        )}
      />

      <Controller
        control={control}
        name={'overlappingEmployment.contact.fEmpOfficeRoom'}
        rules={{
          maxLength: {
            value: TEXT_MAX_LENGTH,
            message: t('laMaxLengthRequired'),
          },
        }}
        render={({ field: { onChange, value }, fieldState: { error } }) => (
          <TextField
            value={value}
            sx={{
              ...sxStyles.selectStyle,
              ...sxStyles.inputBottomMargin,
            }}
            onChange={onChange}
            label={t('fEmpOfficeRoom')}
            error={!!error}
            helperText={error?.message}
          />
        )}
      />
    </Box>
  );
};
